import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Briefcase,
  User,
  Users,
  Calendar,
  FileText,
  DollarSign,
  Clock,
  CheckSquare,
  Play,
  Pause,
  XCircle,
  MapPin,
  Tag,
  Plus,
  X,
  StickyNote,
  Edit3,
  MessageSquare,
  AlertTriangle,
  UserPlus,
  Mail,
  Phone,
  Building2,
  Trash2,
  Milestone,
  FileCheck,
  Database,
  GripVertical,
  Upload,
  Loader2,
  Save,
  Circle,
  CheckCircle2,
  Ban,
  CreditCard,
  Send,
  Home,
  LayoutGrid,
  Handshake,
} from "lucide-react";
import {
  Shield,
  Heart,
  Scale,
  ScrollText,
  Lightbulb,
  Receipt,
  Leaf,
  HardHat,
  ShieldCheck,
  Film,
  Cpu,
  Gavel,
  Stethoscope,

  PenTool,
  Package,
  CalendarClock,
  Archive,
} from "lucide-react";
import { mattersApi, usersApi, immigrationApi, immigrationProgramsApi, invoicesApi, paymentsApi, conversationsApi, clientsApi, realEstateCasesApi, estatesApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import { useAssistantContext } from "../assistant/assistantContextStore";
import PremiumModal from "../ui/PremiumModal";
import { notifyError } from "../ui/Toast";
import { useEmailCompose } from "../../contexts/EmailComposeContext";
import TaskBoard from "../tasks/TaskBoard";
import MatterNotesTab from "./MatterNotesTab";
import SearchableSelect from "../ui/SearchableSelect";
import DocumentChecklistPanel from "./DocumentChecklistPanel";
import PartiesTab from "./PartiesTab";
import UndertakingsTab from "./UndertakingsTab";
import MatterDocumentsTab from "./MatterDocumentsTab";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import RealEstateOverviewTab from "../real-estate/RealEstateOverviewTab";
import RealEstatePipelineTab from "../real-estate/RealEstatePipelineTab";
import RealEstateChecklistTab from "../real-estate/RealEstateChecklistTab";
import RealEstateDocumentsTab from "../real-estate/RealEstateDocumentsTab";
import RealEstateFundsTab from "../real-estate/RealEstateFundsTab";
import RealEstateTitleTab from "../real-estate/RealEstateTitleTab";
import EstatePartiesTab from "../estates/EstatePartiesTab";
import CapacityAssessmentTab from "../estates/CapacityAssessmentTab";
import SigningCeremonyTab from "../estates/SigningCeremonyTab";
import EstateDocumentPackageTab from "../estates/EstateDocumentPackageTab";
import EstateCustodyTab from "../estates/EstateCustodyTab";
import EstateDeadlinesTab from "../estates/EstateDeadlinesTab";

type Tab = string;

interface TabDef {
  id: string;
  label: string;
  Icon: React.FC<any>;
}

const milestoneStatusIcons: Record<string, React.ReactNode> = {
  not_started: <Circle className="h-5 w-5 text-slate-300" />,
  in_progress: <Clock className="h-5 w-5 text-blue-500" />,
  done: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
  blocked: <Ban className="h-5 w-5 text-red-500" />,
};

const MILESTONE_STATUS_CYCLE = ["not_started", "in_progress", "done", "blocked"] as const;
function cycleMilestoneStatus(current: string): string {
  const idx = MILESTONE_STATUS_CYCLE.indexOf(current as any);
  return MILESTONE_STATUS_CYCLE[(idx + 1) % MILESTONE_STATUS_CYCLE.length];
}

const filingOutcomeColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  refused: "bg-red-100 text-red-700",
  withdrawn: "bg-slate-100 text-slate-600",
  returned: "bg-orange-100 text-orange-700",
};

const formatLabel = (s: string) =>
  s?.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

const formatMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

const baseTabs: TabDef[] = [
  { id: "overview", label: "Overview", Icon: Briefcase },
  { id: "tasks", label: "Tasks", Icon: CheckSquare },
  { id: "documents", label: "Documents", Icon: FileText },
  { id: "checklist", label: "Document Checklist", Icon: FileCheck },
  { id: "billing", label: "Billing", Icon: DollarSign },
  { id: "timeline", label: "Timeline", Icon: Clock },
  { id: "parties", label: "Parties", Icon: Users },
  { id: "undertakings", label: "Undertakings", Icon: Handshake },
  { id: "notes", label: "Notes", Icon: StickyNote },
  { id: "communications", label: "Communications", Icon: MessageSquare },
];

// Practice-area-specific tabs that appear dynamically based on matter.practiceAreas
const practiceAreaTabs: Record<string, TabDef[]> = {
  real_estate: [],
  criminal: [
    { id: "criminal-charges", label: "Charges & Plea", Icon: Shield },
    { id: "criminal-court", label: "Court Dates", Icon: Calendar },
    { id: "criminal-bail", label: "Bail & Conditions", Icon: FileText },
    { id: "criminal-disclosure", label: "Disclosure", Icon: FileText },
  ],
  personal_injury: [
    { id: "pi-injuries", label: "Injuries & Treatment", Icon: Stethoscope },
    { id: "pi-insurance", label: "Insurance Claims", Icon: ShieldCheck },
    { id: "pi-damages", label: "Damages", Icon: DollarSign },
    { id: "pi-settlement", label: "Settlement", Icon: FileText },
  ],
  family: [
    { id: "family-parties", label: "Parties & Children", Icon: Heart },
    { id: "family-financial", label: "Financial Disclosure", Icon: DollarSign },
    { id: "family-support", label: "Support", Icon: DollarSign },
    { id: "family-orders", label: "Court Orders", Icon: FileText },
  ],
  litigation: [
    { id: "lit-parties", label: "Case Parties", Icon: Scale },
    { id: "lit-pleadings", label: "Pleadings", Icon: FileText },
    { id: "lit-discovery", label: "Discovery", Icon: FileText },
    { id: "lit-motions", label: "Motions", Icon: FileText },
  ],
  wills_estates: [
    { id: "estates-will", label: "Will & Executor", Icon: ScrollText },
    { id: "estates-beneficiaries", label: "Beneficiaries", Icon: Users },
    { id: "estates-assets", label: "Assets & Liabilities", Icon: DollarSign },
    { id: "estates-distributions", label: "Distributions", Icon: DollarSign },
    { id: "estates-parties", label: "Parties & Roles", Icon: Users },
    { id: "estates-capacity", label: "Capacity Assessment", Icon: ShieldCheck },
    { id: "estates-signing", label: "Signing Ceremony", Icon: PenTool },
    { id: "estates-documents", label: "Document Package", Icon: Package },
    { id: "estates-custody", label: "Custody & Storage", Icon: Archive },
    { id: "estates-deadlines", label: "Deadlines", Icon: CalendarClock },
  ],
  class_action: [
    { id: "ca-defendants", label: "Defendants", Icon: Users },
    { id: "ca-certification", label: "Certification", Icon: FileText },
    { id: "ca-class", label: "Class Members", Icon: Users },
  ],
  corporate: [
    { id: "corp-shareholders", label: "Shareholders & Directors", Icon: Building2 },
    { id: "corp-transactions", label: "Transactions", Icon: DollarSign },
    { id: "corp-filings", label: "Filings", Icon: FileText },
  ],
  employment_labour: [
    { id: "emp-details", label: "Employment Details", Icon: Briefcase },
    { id: "emp-termination", label: "Termination", Icon: FileText },
    { id: "emp-settlement", label: "Settlement", Icon: DollarSign },
  ],
  intellectual_property: [
    { id: "ip-application", label: "Application & Registration", Icon: Lightbulb },
    { id: "ip-prosecution", label: "Prosecution", Icon: FileText },
    { id: "ip-licensing", label: "Licensing", Icon: FileText },
  ],
  tax: [
    { id: "tax-assessment", label: "Assessment", Icon: Receipt },
    { id: "tax-objection", label: "Objection", Icon: FileText },
    { id: "tax-appeal", label: "Court Appeal", Icon: Scale },
  ],
  environmental: [
    { id: "env-site", label: "Site Info", Icon: Leaf },
    { id: "env-permits", label: "Permits", Icon: FileText },
    { id: "env-regulatory", label: "Regulatory Orders", Icon: FileText },
  ],
  bankruptcy_insolvency: [
    { id: "bk-creditors", label: "Creditors", Icon: Users },
    { id: "bk-proposals", label: "Proposals", Icon: FileText },
    { id: "bk-filings", label: "Filings", Icon: FileText },
  ],
  administrative: [
    { id: "admin-hearings", label: "Hearings", Icon: Gavel },
    { id: "admin-submissions", label: "Submissions", Icon: FileText },
  ],
  construction: [
    { id: "const-claims", label: "Claims & Liens", Icon: HardHat },
    { id: "const-adjudication", label: "Adjudication", Icon: Scale },
  ],
  insurance: [
    { id: "ins-coverage", label: "Coverage", Icon: ShieldCheck },
    { id: "ins-demands", label: "Demands & Offers", Icon: DollarSign },
  ],
  entertainment: [
    { id: "ent-rights", label: "Rights", Icon: Film },
    { id: "ent-royalties", label: "Royalties", Icon: DollarSign },
  ],
  technology: [
    { id: "tech-privacy", label: "Privacy & Compliance", Icon: Cpu },
    { id: "tech-incidents", label: "Incidents", Icon: AlertTriangle },
  ],
  immigration: [
    { id: "imm-milestones", label: "Milestones", Icon: Milestone },
    { id: "imm-filings", label: "Filings", Icon: FileText },
    { id: "imm-parties", label: "Parties & Dependents", Icon: Users },
    { id: "imm-documents", label: "Document Checklist", Icon: FileCheck },
    { id: "imm-program-data", label: "Program Data", Icon: Database },
  ],
};

const practiceAreaColors: Record<string, string> = {
  real_estate: "bg-cyan-100 text-cyan-700",
  family: "bg-pink-100 text-pink-700",
  immigration: "bg-teal-100 text-teal-700",        // was emerald — conflicts with "Active" status
  criminal: "bg-red-100 text-red-700",
  corporate: "bg-purple-100 text-purple-700",
  litigation: "bg-blue-100 text-blue-700",
  wills_estates: "bg-indigo-100 text-indigo-700",
  personal_injury: "bg-orange-100 text-orange-700",
  employment_labour: "bg-sky-100 text-sky-700",    // was amber — conflicts with "on_hold" status
  intellectual_property: "bg-yellow-100 text-yellow-700",
  tax: "bg-lime-100 text-lime-700",
  environmental: "bg-green-100 text-green-700",
  bankruptcy_insolvency: "bg-rose-100 text-rose-700",
  administrative: "bg-slate-200 text-slate-700",
  construction: "bg-stone-100 text-stone-700",
  insurance: "bg-fuchsia-100 text-fuchsia-700",
  entertainment: "bg-violet-100 text-violet-700",
  technology: "bg-blue-100 text-blue-800",
  other: "bg-slate-100 text-slate-600",
};

const statusColors: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  on_hold: "bg-amber-100 text-amber-700",
};

const formatPracticeArea = (pa: string) =>
  pa?.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

const formatStatus = (s: string) =>
  s === "on_hold" ? "On Hold" : s === "open" ? "Active" : s?.charAt(0).toUpperCase() + s?.slice(1);

const dateKindBadge: Record<string, string> = {
  deadline: "bg-red-100 text-red-700",
  hearing: "bg-blue-100 text-blue-700",
  closing: "bg-cyan-100 text-cyan-700",
  filing: "bg-indigo-100 text-indigo-700",
  meeting: "bg-purple-100 text-purple-700",
  other: "bg-slate-100 text-slate-600",
};

const ALL_PRACTICE_AREAS = [
  { value: "real_estate", label: "Real Estate" },
  { value: "family", label: "Family Law" },
  { value: "immigration", label: "Immigration" },
  { value: "criminal", label: "Criminal Defence" },
  { value: "corporate", label: "Corporate & Commercial" },
  { value: "litigation", label: "Civil Litigation" },
  { value: "wills_estates", label: "Wills, Estates & Trusts" },
  { value: "personal_injury", label: "Personal Injury" },
  { value: "employment_labour", label: "Employment & Labour" },
  { value: "intellectual_property", label: "Intellectual Property" },
  { value: "tax", label: "Tax Law" },
  { value: "environmental", label: "Environmental Law" },
  { value: "bankruptcy_insolvency", label: "Bankruptcy & Insolvency" },
  { value: "administrative", label: "Administrative Law" },
  { value: "construction", label: "Construction Law" },
  { value: "insurance", label: "Insurance Law" },
  { value: "entertainment", label: "Entertainment & Media" },
  { value: "technology", label: "Technology & Cyber" },
  { value: "other", label: "Other" },
];

const getInitials = (name: string | undefined) =>
  name
    ?.split(" ")
    .slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase())
    .join("") || "?";

const MatterDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openCompose } = useEmailCompose();
  const [searchParams] = useSearchParams();
  const { userData, firmData, canManageBilling } = useUserDataContext();
  const [matter, setMatter] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<Tab>(searchParams.get("tab") || "overview");
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState<any>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [titleError, setTitleError] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [editingMatterNumber, setEditingMatterNumber] = useState(false);
  const [matterNumberValue, setMatterNumberValue] = useState("");
  const [matterNumberError, setMatterNumberError] = useState("");

  // Summary state
  const [summary, setSummary] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Team management state
  const [firmUsers, setFirmUsers] = useState<any[]>([]);
  const [addingTeamMember, setAddingTeamMember] = useState(false);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = userData?._id === firmData?.adminUserId;

  // Immigration case data
  const [immigrationCase, setImmigrationCase] = useState<any>(null);
  const [immSaving, setImmSaving] = useState(false);

  // Real estate case data
  const [realEstateCase, setRealEstateCase] = useState<any>(null);

  // Estates case data
  const [estateCase, setEstateCase] = useState<any>(null);

  // Immigration forms
  const [showPartyForm, setShowPartyForm] = useState(false);
  const [partyForm, setPartyForm] = useState({ role: "principal_applicant", displayName: "", email: "", phone: "", citizenshipCountry: "", residenceCountry: "", dateOfBirth: "", passportNumber: "" });
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState({ key: "", label: "", dueAt: "" });
  const [editingMilestoneIdx, setEditingMilestoneIdx] = useState<number | null>(null);
  const [editMilestoneLabel, setEditMilestoneLabel] = useState("");
  const [showFilingForm, setShowFilingForm] = useState(false);
  const [filingForm, setFilingForm] = useState({ jurisdiction: { country: "CA", region: "" }, authority: "IRCC", category: "application" as string, submissionMethod: "online_portal" as string, notes: "" });
  const [programData, setProgramData] = useState<any>({ data: {}, derived: {} });
  const [programSchema, setProgramSchema] = useState<any[]>([]);

  // Document checklist state
  const [checklist, setChecklist] = useState<any[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [addEmailValue, setAddEmailValue] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [addEmailError, setAddEmailError] = useState("");

  // Matter documents state

  // Billing state
  const [invoices, setInvoices] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ source: "cheque" as string, amountDollars: "", invoiceId: "", chequeNumber: "", eftReference: "", receivedFrom: "", notes: "" });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [creatingPayment, setCreatingPayment] = useState(false);

  // Communications state
  const [clientConversations, setClientConversations] = useState<Record<string, any[]>>({});
  const [commsLoading, setCommsLoading] = useState(false);

  const fetchMatter = useCallback(async () => {
    if (!id) return;
    try {
      const res = await mattersApi.get(id);
      setMatter(res.data);
    } catch (err) {
      console.error("Failed to fetch matter:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSummary = useCallback(async () => {
    if (!id) return;
    setLoadingSummary(true);
    try {
      const res = await mattersApi.getSummary(id);
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch matter summary:", err);
    } finally {
      setLoadingSummary(false);
    }
  }, [id]);

  const fetchFirmUsers = useCallback(async () => {
    try {
      const res = await usersApi.list();
      setFirmUsers(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch firm users:", err);
    }
  }, []);

  const fetchTimeline = useCallback(async () => {
    if (!id) return;
    try {
      const res = await mattersApi.timeline(id);
      setTimeline(res.data);
    } catch (err) {
      console.error("Failed to fetch timeline:", err);
    }
  }, [id]);

  useEffect(() => {
    fetchMatter();
    fetchSummary();
    fetchFirmUsers();
  }, [fetchMatter, fetchSummary, fetchFirmUsers]);

  useEffect(() => {
    if (activeTab === "timeline") fetchTimeline();
  }, [activeTab, fetchTimeline]);

  // Sync activeTab when URL ?tab= param changes (e.g. from matter inspector drawer)
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useAssistantContext(matter?._id ? { type: "matter", id: matter._id, name: matter.title } : null);

  useEffect(() => {
    if (!matter) return;
    if (matter.title) setTitleValue(matter.title);
    if (matter.matterNumber) setMatterNumberValue(matter.matterNumber);
    if (matter.description !== undefined) setDescriptionValue(matter.description || "");
  }, [matter]);

  // Build the resolved practice areas list (same fallback logic as tab builder)
  const matterAreas = (matter?.practiceAreas || [matter?.practiceArea]).filter(Boolean) as string[];

  // Fetch immigration case linked to this matter
  const hasImmigration = matterAreas.includes("immigration");
  const fetchImmigrationCase = useCallback(async () => {
    if (!id || !hasImmigration) return;
    try {
      const res = await immigrationApi.listCases({ matterId: id, limit: 1 });
      setImmigrationCase(res.data?.data?.[0] || null);
    } catch (err) {
      console.error("Failed to fetch immigration case:", err);
    }
  }, [id, hasImmigration]);

  useEffect(() => {
    if (hasImmigration) {
      fetchImmigrationCase();
    }
  }, [hasImmigration, fetchImmigrationCase]);

  // Fetch real estate case linked to this matter
  const hasRealEstate = matterAreas.includes("real_estate");
  const fetchRealEstateCase = useCallback(async () => {
    if (!id || !hasRealEstate) return;
    try {
      const res = await realEstateCasesApi.list({ matterId: id, limit: 1 });
      setRealEstateCase(res.data?.data?.[0] || null);
    } catch (err) {
      console.error("Failed to fetch real estate case:", err);
    }
  }, [id, hasRealEstate]);

  useEffect(() => {
    if (hasRealEstate) {
      fetchRealEstateCase();
    }
  }, [hasRealEstate, fetchRealEstateCase]);

  // Fetch estates case data linked to this matter (auto-create if missing)
  const hasEstates = matterAreas.includes("wills_estates");
  const fetchEstateCase = useCallback(async () => {
    if (!id || !hasEstates) return;
    try {
      const res = await estatesApi.list({ matterId: id, limit: 1 });
      const existing = res.data?.data?.[0] || null;
      if (existing) {
        setEstateCase(existing);
      } else {
        // Auto-create estate case data if matter has wills_estates practice area
        const createRes = await estatesApi.create({ matterId: id, caseType: "estate_planning" });
        setEstateCase(createRes.data);
      }
    } catch (err) {
      console.error("Failed to fetch estate case:", err);
    }
  }, [id, hasEstates]);

  useEffect(() => {
    if (hasEstates) {
      fetchEstateCase();
    }
  }, [hasEstates, fetchEstateCase]);

  // Fetch document checklist when checklist tab is active
  useEffect(() => {
    if ((activeTab === "checklist" || activeTab === "imm-documents") && id) {
      if (checklist.length === 0) setChecklistLoading(true);
      mattersApi.getDocumentChecklist(id)
        .then((res) => setChecklist(res.data || []))
        .catch(() => setChecklist([]))
        .finally(() => setChecklistLoading(false));
    }
  }, [activeTab, id]);

  // Fetch matter documents when documents tab is active

  // Fetch billing data when billing tab is active
  useEffect(() => {
    if (activeTab === "billing" && id) {
      if (invoices.length === 0 && payments.length === 0) setBillingLoading(true);
      Promise.all([
        invoicesApi.list({ matterId: id, limit: 200 }),
        paymentsApi.list({ matterId: id, limit: 200 }),
      ])
        .then(([invRes, payRes]) => {
          setInvoices(invRes.data?.data || []);
          setPayments(payRes.data?.data || []);
        })
        .catch(() => {})
        .finally(() => setBillingLoading(false));
    }
  }, [activeTab, id]);

  // Fetch program data & schema for immigration program-data tab
  useEffect(() => {
    if (activeTab === "imm-program-data" && immigrationCase?._id) {
      immigrationApi.getProgramData(immigrationCase._id).then((res) => {
        setProgramData(res.data);
      }).catch(() => {});

      immigrationProgramsApi.list({ search: "", limit: 100 }).then((res) => {
        const prog = (res.data.data || []).find(
          (p: any) => p.programKey === immigrationCase.program?.programKey
        );
        if (prog) setProgramSchema(prog.schemaFields || []);
      }).catch(() => {});
    }
  }, [activeTab, immigrationCase]);

  // Fetch communications when communications tab is active
  useEffect(() => {
    if (activeTab === "communications" && matter) {
      if (Object.keys(clientConversations).length === 0) setCommsLoading(true);
      const clientIds: string[] = [];
      const primaryClientId = typeof matter.clientId === "object" ? matter.clientId._id : matter.clientId;
      if (primaryClientId) clientIds.push(primaryClientId);
      (matter.clientLinks || []).forEach((link: any) => {
        const cid = typeof link.clientId === "object" ? link.clientId._id : link.clientId;
        if (cid && !clientIds.includes(cid)) clientIds.push(cid);
      });

      Promise.all(
        clientIds.map((cid) =>
          conversationsApi.list({ clientId: cid, limit: 10 })
            .then((res) => ({ clientId: cid, conversations: res.data?.data || [] }))
            .catch(() => ({ clientId: cid, conversations: [] }))
        )
      ).then((results) => {
        const map: Record<string, any[]> = {};
        results.forEach((r) => { map[r.clientId] = r.conversations; });
        setClientConversations(map);
      }).finally(() => setCommsLoading(false));
    }
  }, [activeTab, matter]);

  const handleTitleSave = async () => {
    const trimmed = titleValue.trim();
    if (!id || !trimmed || trimmed === matter.title) {
      setEditingTitle(false);
      setTitleValue(matter.title);
      setTitleError("");
      return;
    }
    try {
      await mattersApi.update(id, { title: trimmed });
      setEditingTitle(false);
      setTitleError("");
      fetchMatter();
    } catch (err: any) {
      const resp = err?.response;
      if (resp?.status === 409 && resp?.data?.error === "DUPLICATE_TITLE") {
        setTitleError(resp.data.message);
      } else {
        console.error("Failed to update title:", err);
        notifyError("Couldn't rename the matter.", err);
        setEditingTitle(false);
        setTitleValue(matter.title);
      }
    }
  };

  const handleMatterNumberSave = async () => {
    const trimmed = matterNumberValue.trim();
    if (!id || !trimmed || trimmed === matter.matterNumber) {
      setEditingMatterNumber(false);
      setMatterNumberValue(matter.matterNumber);
      setMatterNumberError("");
      return;
    }
    try {
      await mattersApi.update(id, { matterNumber: trimmed });
      setEditingMatterNumber(false);
      setMatterNumberError("");
      fetchMatter();
    } catch (err: any) {
      const resp = err?.response;
      if (resp?.status === 409 && resp?.data?.error === "DUPLICATE_MATTER_NUMBER") {
        setMatterNumberError(resp.data.message);
      } else {
        console.error("Failed to update matter number:", err);
        notifyError("Couldn't update the matter number.", err);
        setEditingMatterNumber(false);
        setMatterNumberValue(matter.matterNumber);
      }
    }
  };


  const handleDescriptionSave = async () => {
    if (!id) return;
    const trimmed = descriptionValue.trim();
    if (trimmed === (matter.description || "")) return;
    try {
      await mattersApi.update(id, { description: trimmed || null });
      setMatter((prev: any) => ({ ...prev, description: trimmed || null }));
    } catch (err) {
      console.error("Failed to update description:", err);
      notifyError("Couldn't save the description.", err);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      await mattersApi.changeStatus(id, newStatus);
      fetchMatter();
    } catch (err) {
      console.error("Failed to change status:", err);
      notifyError("Couldn't change the matter status.", err);
    }
  };

  const handleDelete = async () => {
    if (!id || !isAdmin) return;
    setDeleting(true);
    try {
      await mattersApi.remove(id);
      navigate("/matters");
    } catch (err) {
      console.error("Failed to delete matter:", err);
      notifyError("Couldn't delete the matter.", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddTeamMember = async (userId: string) => {
    if (!id || !userId) return;
    const currentIds = (matter.assignedUserIds || []).map((u: any) =>
      typeof u === "object" ? u._id : u
    );
    if (currentIds.includes(userId)) return;
    try {
      await mattersApi.assign(id, { assignedUserIds: [...currentIds, userId] });
      setAddingTeamMember(false);
      fetchMatter();
    } catch (err) {
      console.error("Failed to add team member:", err);
      notifyError("Couldn't add the team member.", err);
    }
  };

  const handleRemoveTeamMember = async (userId: string) => {
    if (!id) return;
    const currentIds = (matter.assignedUserIds || []).map((u: any) =>
      typeof u === "object" ? u._id : u
    );
    try {
      await mattersApi.assign(id, {
        assignedUserIds: currentIds.filter((x: string) => x !== userId),
      });
      fetchMatter();
    } catch (err) {
      console.error("Failed to remove team member:", err);
      notifyError("Couldn't remove the team member.", err);
    }
  };

  // ─── Immigration handlers ───
  const immCaseId = immigrationCase?._id;

  const handleAddParty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!immCaseId) return;
    setImmSaving(true);
    try {
      await immigrationApi.addParty(immCaseId, partyForm);
      await fetchImmigrationCase();
      setShowPartyForm(false);
      setPartyForm({ role: "principal_applicant", displayName: "", email: "", phone: "", citizenshipCountry: "", residenceCountry: "", dateOfBirth: "", passportNumber: "" });
    } catch (err) {
      console.error("Failed to add party:", err);
      notifyError("Couldn't add the party.", err);
    } finally {
      setImmSaving(false);
    }
  };

  const handleRemoveParty = async (index: number) => {
    if (!immCaseId) return;
    try {
      await immigrationApi.removeParty(immCaseId, index);
      await fetchImmigrationCase();
    } catch (err) {
      console.error("Failed to remove party:", err);
      notifyError("Couldn't remove the party.", err);
    }
  };

  const handleMilestoneStatus = async (index: number, status: string) => {
    if (!immCaseId) return;
    try {
      await immigrationApi.updateMilestone(immCaseId, index, { status });
      await fetchImmigrationCase();
    } catch (err) {
      console.error("Failed to update milestone:", err);
      notifyError("Couldn't update the milestone.", err);
    }
  };

  const handleAddMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!immCaseId) return;
    setImmSaving(true);
    try {
      await immigrationApi.addMilestone(immCaseId, milestoneForm);
      await fetchImmigrationCase();
      setShowMilestoneForm(false);
      setMilestoneForm({ key: "", label: "", dueAt: "" });
    } catch (err) {
      console.error("Failed to add milestone:", err);
      notifyError("Couldn't add the milestone.", err);
    } finally {
      setImmSaving(false);
    }
  };

  const handleEditMilestoneLabel = async (index: number) => {
    if (!immCaseId || !editMilestoneLabel.trim()) return;
    try {
      await immigrationApi.updateMilestone(immCaseId, index, { label: editMilestoneLabel.trim() });
      await fetchImmigrationCase();
      setEditingMilestoneIdx(null);
    } catch (err) {
      console.error("Failed to edit milestone:", err);
      notifyError("Couldn't rename the milestone.", err);
    }
  };

  const handleRemoveMilestone = async (index: number) => {
    if (!immCaseId) return;
    try {
      await immigrationApi.removeMilestone(immCaseId, index);
      await fetchImmigrationCase();
    } catch (err) {
      console.error("Failed to remove milestone:", err);
      notifyError("Couldn't remove the milestone.", err);
    }
  };

  const handleMilestoneReorder = async (result: any) => {
    if (!result.destination || !immCaseId || !immigrationCase) return;
    const items = Array.from(immigrationCase.milestones);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const oldMilestones = immigrationCase.milestones;
    const order = items.map((item: any) => oldMilestones.indexOf(item));
    setImmigrationCase((prev: any) => ({ ...prev, milestones: items }));
    try {
      await immigrationApi.reorderMilestones(immCaseId, order);
      await fetchImmigrationCase();
    } catch (err) {
      console.error("Failed to reorder milestones:", err);
      notifyError("Couldn't reorder milestones.", err);
      await fetchImmigrationCase();
    }
  };

  const handleAddFiling = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!immCaseId) return;
    setImmSaving(true);
    try {
      await immigrationApi.addFiling(immCaseId, filingForm);
      await fetchImmigrationCase();
      setShowFilingForm(false);
      setFilingForm({ jurisdiction: { country: "CA", region: "" }, authority: "IRCC", category: "application", submissionMethod: "online_portal", notes: "" });
    } catch (err) {
      console.error("Failed to add filing:", err);
      notifyError("Couldn't add the filing.", err);
    } finally {
      setImmSaving(false);
    }
  };

  const handleRemoveFiling = async (index: number) => {
    if (!immCaseId) return;
    try {
      await immigrationApi.removeFiling(immCaseId, index);
      await fetchImmigrationCase();
    } catch (err) {
      console.error("Failed to remove filing:", err);
      notifyError("Couldn't remove the filing.", err);
    }
  };

  const handleSaveProgramData = async () => {
    if (!immCaseId) return;
    setImmSaving(true);
    try {
      await immigrationApi.updateProgramData(immCaseId, programData);
    } catch (err) {
      console.error("Failed to save program data:", err);
      notifyError("Couldn't save program details.", err);
    } finally {
      setImmSaving(false);
    }
  };

  const handleAddClientEmail = async () => {
    if (!addEmailValue.trim() || !client?._id) return;
    setAddingEmail(true);
    setAddEmailError("");
    try {
      const existingEmails = client?.emails || [];
      await clientsApi.update(client._id, {
        emails: [...existingEmails, { label: "main", email: addEmailValue.trim() }],
      });
      const res = await mattersApi.get(id!);
      setMatter(res.data);
      setShowAddEmailModal(false);
      setAddEmailValue("");
      setAddEmailError("");
    } catch (err: any) {
      setAddEmailError(err?.response?.data?.error || "Failed to add email");
    } finally {
      setAddingEmail(false);
    }
  };

  // ─── Billing handlers ───
  const handleRecordPayment = async () => {
    const clientId = typeof matter?.clientId === "object" ? matter.clientId._id : matter?.clientId;
    if (!id || !clientId) return;
    setCreatingPayment(true);
    try {
      const amountCents = Math.round(parseFloat(paymentForm.amountDollars || "0") * 100);
      if (amountCents <= 0) return;
      const payload: Record<string, any> = {
        matterId: id,
        clientId,
        source: paymentForm.source,
        amountCents,
        invoiceId: paymentForm.invoiceId || null,
        methodDetails: {
          chequeNumber: paymentForm.chequeNumber || undefined,
          eftReference: paymentForm.eftReference || undefined,
          receivedFrom: paymentForm.receivedFrom || undefined,
          notes: paymentForm.notes || undefined,
        },
      };
      const res = await paymentsApi.create(payload);
      if (receiptFile && res.data?._id) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        await paymentsApi.addAttachment(res.data._id, fd);
      }
      const [invRes, payRes] = await Promise.all([
        invoicesApi.list({ matterId: id, limit: 200 }),
        paymentsApi.list({ matterId: id, limit: 200 }),
      ]);
      setInvoices(invRes.data?.data || []);
      setPayments(payRes.data?.data || []);
      setShowRecordPayment(false);
      setPaymentForm({ source: "cheque", amountDollars: "", invoiceId: "", chequeNumber: "", eftReference: "", receivedFrom: "", notes: "" });
      setReceiptFile(null);
    } catch (err) {
      console.error("Failed to record payment:", err);
      notifyError("Couldn't record the payment.", err);
    } finally {
      setCreatingPayment(false);
    }
  };

  // Tabs to hide per practice area (simplification)
  const hiddenTabsByPracticeArea: Record<string, string[]> = {
    real_estate: ["timeline", "notes"],
  };

  // Tab reordering per practice area (move important tabs up)
  const tabOrderByPracticeArea: Record<string, string[]> = {
    real_estate: ["overview", "checklist", "documents", "tasks", "parties", "undertakings", "billing", "communications"],
  };

  // Build dynamic tabs based on matter's practice areas
  // (Must be called before early returns to satisfy React hooks rules)
  const allTabs = useMemo(() => {
    if (!matter) return baseTabs;
    const dynamicTabs: TabDef[] = [];
    const matterAreas = (matter?.practiceAreas || [matter?.practiceArea]).filter(Boolean);
    for (const pa of matterAreas) {
      const extra = practiceAreaTabs[pa];
      if (extra) dynamicTabs.push(...extra);
    }
    let base = [...baseTabs];
    if (dynamicTabs.length > 0) {
      const notesIdx = base.findIndex((t) => t.id === "notes");
      base.splice(notesIdx, 0, ...dynamicTabs);
    }

    // Hide tabs for specific practice areas
    const primaryArea = matterAreas[0];
    const hidden = hiddenTabsByPracticeArea[primaryArea];
    if (hidden) {
      base = base.filter((t) => !hidden.includes(t.id));
    }

    // Reorder tabs for specific practice areas
    const order = tabOrderByPracticeArea[primaryArea];
    if (order) {
      const ordered: TabDef[] = [];
      for (const id of order) {
        const tab = base.find((t) => t.id === id);
        if (tab) ordered.push(tab);
      }
      // Append any remaining tabs not in the explicit order
      for (const tab of base) {
        if (!order.includes(tab.id)) ordered.push(tab);
      }
      base = ordered;
    }

    return base;
  }, [matter?.practiceAreas, matter?.practiceArea, matter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!matter) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Matter not found</p>
        <button
          onClick={() => navigate("/matters")}
          className="mt-4 text-brand font-medium text-sm hover:underline"
        >
          Back to Matters
        </button>
      </div>
    );
  }

  const client = typeof matter.clientId === "object" ? matter.clientId : null;
  const lawyer = typeof matter.responsibleLawyerId === "object" ? matter.responsibleLawyerId : null;
  const assignedUsers = matter.assignedUserIds || [];

  // Build team member select options (exclude already assigned + responsible lawyer)
  const assignedIds = assignedUsers.map((u: any) => (typeof u === "object" ? u._id : u));
  const lawyerId = lawyer?._id;
  const teamMemberOptions = firmUsers
    .filter((u) => u._id !== lawyerId && !assignedIds.includes(u._id))
    .map((u) => ({ value: u._id, label: u.name || u.email }));

  return (
    <div>
      {/* Back + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/matters")}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Matters
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Matter/File # — label + editable number inline */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">
                Matter/File #
              </span>
              {editingMatterNumber ? (
                <div>
                  <input
                    autoFocus
                    value={matterNumberValue}
                    onChange={(e) => {
                      setMatterNumberValue(e.target.value);
                      if (matterNumberError) setMatterNumberError("");
                    }}
                    onBlur={handleMatterNumberSave}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleMatterNumberSave();
                      } else if (e.key === "Escape") {
                        setEditingMatterNumber(false);
                        setMatterNumberValue(matter.matterNumber);
                        setMatterNumberError("");
                      }
                    }}
                    className={`text-sm font-mono text-slate-600 border-b-2 ${matterNumberError ? "border-red-400" : "border-emerald-600"} focus:outline-none bg-transparent`}
                  />
                  <AnimatePresence>
                    {matterNumberError && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="flex items-center gap-2 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <span className="text-sm text-red-700 flex-1">{matterNumberError}</span>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditingMatterNumber(false);
                            setMatterNumberValue(matter.matterNumber);
                            setMatterNumberError("");
                          }}
                          className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <span
                  className="group inline-flex items-center gap-1 text-sm font-mono text-slate-400 cursor-pointer hover:text-slate-600 transition-colors"
                  onClick={() => setEditingMatterNumber(true)}
                >
                  {matter.matterNumber}
                  <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              )}
            </div>
            {/* Status + practice area tags */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  statusColors[matter.status] || "bg-slate-100 text-slate-600"
                }`}
              >
                {formatStatus(matter.status)}
              </span>
              {(matter.practiceAreas || [matter.practiceArea]).filter(Boolean).map((pa: string) => (
                <span
                  key={pa}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    practiceAreaColors[pa] || "bg-slate-100 text-slate-600"
                  }`}
                >
                  {formatPracticeArea(pa)}
                </span>
              ))}
            </div>
            {editingTitle ? (
              <div className="w-full">
                <input
                  autoFocus
                  value={titleValue}
                  onChange={(e) => {
                    setTitleValue(e.target.value);
                    if (titleError) setTitleError("");
                  }}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleTitleSave();
                    } else if (e.key === "Escape") {
                      setEditingTitle(false);
                      setTitleValue(matter.title);
                      setTitleError("");
                    }
                  }}
                  className={`text-2xl font-bold text-slate-800 border-b-2 ${titleError ? "border-red-400" : "border-emerald-600"} focus:outline-none bg-transparent w-full`}
                />
                <AnimatePresence>
                  {titleError && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-center gap-2 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-red-700 flex-1">{titleError}</span>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setEditingTitle(false);
                          setTitleValue(matter.title);
                          setTitleError("");
                        }}
                        className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div
                className="group flex items-center gap-2 cursor-pointer"
                onClick={() => setEditingTitle(true)}
              >
                <h1 className="text-2xl font-bold text-slate-800">{matter.title}</h1>
                <Edit3 className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}

            {/* Client & Opposing Counsel Cards */}
            <div className="mt-3 flex flex-wrap items-start gap-3">
              {/* Prominent Client Card */}
              {client && (
                <div
                  onClick={() => navigate(`/clients/${client._id}`)}
                  className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-200 cursor-pointer hover:border-brand-light hover:shadow-md transition-all group"
                >
                  <div
                    className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                    style={{ backgroundColor: "var(--color-accent)" }}
                  >
                    {getInitials(client.displayName)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 group-hover:text-brand transition-colors truncate">
                        {client.displayName}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex-shrink-0">
                        {client.type === "organization" ? (
                          <Building2 className="h-3 w-3" />
                        ) : (
                          <User className="h-3 w-3" />
                        )}
                        {client.type?.charAt(0).toUpperCase() + client.type?.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {client.emails?.[0]?.email && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Mail className="h-3 w-3" />
                          {client.emails[0].email}
                        </span>
                      )}
                      {client.phones?.[0]?.number && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Phone className="h-3 w-3" />
                          {client.phones[0].number}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Other Side Counsel Card (Real Estate only) */}
              {realEstateCase?.parties?.otherSideCounsel?.lawyerName && (
                <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-amber-200 transition-all">
                  <div className="h-12 w-12 rounded-full flex items-center justify-center bg-amber-100 text-amber-700 font-bold text-base flex-shrink-0">
                    <Scale className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 truncate">
                        {realEstateCase.parties.otherSideCounsel.lawyerName}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex-shrink-0">
                        Opposing Counsel
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {realEstateCase.parties.otherSideCounsel.firmName && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Building2 className="h-3 w-3" />
                          {realEstateCase.parties.otherSideCounsel.firmName}
                        </span>
                      )}
                      {realEstateCase.parties.otherSideCounsel.email && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Mail className="h-3 w-3" />
                          {realEstateCase.parties.otherSideCounsel.email}
                        </span>
                      )}
                      {realEstateCase.parties.otherSideCounsel.phone && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Phone className="h-3 w-3" />
                          {realEstateCase.parties.otherSideCounsel.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status change buttons */}
          <div className="flex gap-2">
            {matter.status !== "open" && (
              <button
                onClick={() => handleStatusChange("open")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
              >
                <Play className="h-4 w-4" />
                Active
              </button>
            )}
            {matter.status !== "on_hold" && (
              <button
                onClick={() => handleStatusChange("on_hold")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <Pause className="h-4 w-4" />
                On Hold
              </button>
            )}
            {matter.status !== "closed" && (
              <button
                onClick={() => handleStatusChange("closed")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <XCircle className="h-4 w-4" />
                Close
              </button>
            )}
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {allTabs.map((tab) => {
            const active = activeTab === tab.id;
            const Icon = tab.Icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  active ? "text-brand" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {active && (
                  <motion.div
                    layoutId="matterTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Row 1: Tasks Summary | Billing Summary | Recent Messages */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tasks Summary Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <CheckSquare className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Tasks
                  </h3>
                  {summary ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-3xl font-bold text-slate-800">{summary.tasks.openCount}</p>
                          <p className="text-sm text-slate-500">Open tasks</p>
                        </div>
                        {summary.tasks.overdueCount > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-full">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                            <span className="text-sm font-medium text-red-700">
                              {summary.tasks.overdueCount} overdue
                            </span>
                          </div>
                        )}
                      </div>
                      {summary.tasks.upcoming?.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-slate-100">
                          <p className="text-xs text-slate-400 uppercase font-semibold">Upcoming</p>
                          {summary.tasks.upcoming.map((task: any, i: number) => (
                            <div key={task._id || i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-700 truncate">{task.title}</span>
                              <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                                {task.dueAt
                                  ? new Date(task.dueAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="animate-pulse space-y-3">
                      <div className="h-10 bg-slate-100 rounded-lg" />
                      <div className="h-6 bg-slate-100 rounded-lg w-2/3" />
                    </div>
                  )}
                </div>

                {/* Billing Summary Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Billing
                  </h3>
                  {summary ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Outstanding</p>
                        <p className="text-2xl font-bold text-slate-800">
                          ${(summary.billing.outstandingBalanceCents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-slate-500">{summary.billing.outstandingInvoiceCount} invoice(s)</p>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Trust Balance</p>
                        <p className={`text-xl font-bold ${summary.billing.trustBalanceCents >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                          ${(Math.abs(summary.billing.trustBalanceCents) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                          {summary.billing.trustBalanceCents < 0 && " (deficit)"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="animate-pulse space-y-3">
                      <div className="h-10 bg-slate-100 rounded-lg" />
                      <div className="h-6 bg-slate-100 rounded-lg w-1/2" />
                    </div>
                  )}
                </div>

                {/* Recent Messages Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Recent Messages
                  </h3>
                  {summary ? (
                    summary.conversations?.length > 0 ? (
                      <div className="space-y-3">
                        {summary.conversations.map((conv: any, i: number) => (
                          <div key={conv._id || i} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-slate-500 uppercase">
                                {conv.channels?.primaryChannel || "message"}
                              </span>
                              <span className="text-xs text-slate-400">
                                {conv.lastMessageAt
                                  ? new Date(conv.lastMessageAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  : ""}
                              </span>
                            </div>
                            <p className="text-sm text-slate-700 truncate">{conv.preview || conv.subject || "No preview"}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col items-center py-6"
                      >
                        <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 mb-3">
                          <MessageSquare className="h-6 w-6" style={{ color: "var(--color-primary)" }} />
                        </div>
                        <p className="text-sm font-medium text-slate-600 mb-1">No messages yet</p>
                        <p className="text-xs text-slate-400">Conversations will appear here</p>
                      </motion.div>
                    )
                  ) : (
                    <div className="animate-pulse space-y-3">
                      <div className="h-16 bg-slate-100 rounded-lg" />
                      <div className="h-16 bg-slate-100 rounded-lg" />
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Team Management | Important Dates */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Team Management Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Users className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                      Team
                    </h3>
                    {!addingTeamMember && (
                      <button
                        onClick={() => setAddingTeamMember(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                        Add Member
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {/* Responsible Team Member */}
                    {lawyer && (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div
                          className="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                          style={{ backgroundColor: "var(--color-primary)" }}
                        >
                          {getInitials(lawyer.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{lawyer.name}</p>
                          <p className="text-xs text-slate-500">Responsible Team Member</p>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 flex-shrink-0">
                          Lead
                        </span>
                      </div>
                    )}

                    {/* Assigned Users */}
                    {assignedUsers.map((u: any) => {
                      const userId = typeof u === "object" ? u._id : u;
                      const userName = typeof u === "object" ? u.name : "Unknown";
                      return (
                        <div key={userId} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group">
                          <div className="h-9 w-9 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 font-semibold text-xs flex-shrink-0">
                            {getInitials(userName)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{userName}</p>
                            <p className="text-xs text-slate-500">Assigned</p>
                          </div>
                          <button
                            onClick={() => handleRemoveTeamMember(userId)}
                            className="p-1 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                            title="Remove team member"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}

                    {!lawyer && assignedUsers.length === 0 && !addingTeamMember && (
                      <p className="text-sm text-slate-400">No team members assigned</p>
                    )}

                    {/* Add Team Member Select */}
                    {addingTeamMember && (
                      <div className="pt-2 border-t border-slate-100 mt-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <SearchableSelect
                              options={teamMemberOptions}
                              value={null}
                              onChange={(val) => {
                                if (val) handleAddTeamMember(val);
                              }}
                              placeholder="Search team members..."
                            />
                          </div>
                          <button
                            onClick={() => setAddingTeamMember(false)}
                            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Important Dates */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Calendar className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Important Dates
                  </h3>
                  {(matter.importantDates || []).length > 0 ? (
                    <div className="space-y-3">
                      {matter.importantDates.map((d: any, i: number) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium text-slate-800">{d.label}</p>
                              <p className="text-xs text-slate-500">
                                {new Date(d.at).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              dateKindBadge[d.kind] || dateKindBadge.other
                            }`}
                          >
                            {d.kind?.charAt(0).toUpperCase() + d.kind?.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No important dates</p>
                  )}
                </div>
              </div>

              {/* Row 3: Details/References | Tags */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Details / References */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Details</h3>
                  <div className="space-y-4">
                    {matter.references?.externalFileNumber && (
                      <div>
                        <p className="text-xs text-slate-400 uppercase mb-1">External File Number</p>
                        <p className="text-sm text-slate-700">{matter.references.externalFileNumber}</p>
                      </div>
                    )}
                    {matter.references?.governmentRef && (
                      <div>
                        <p className="text-xs text-slate-400 uppercase mb-1">Government Ref</p>
                        <p className="text-sm text-slate-700">{matter.references.governmentRef}</p>
                      </div>
                    )}
                    {matter.references?.property?.addressText && (
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                        <p className="text-sm text-slate-700">{matter.references.property.addressText}</p>
                      </div>
                    )}
                    {!matter.references?.externalFileNumber &&
                      !matter.references?.governmentRef &&
                      !matter.references?.property?.addressText && (
                        <p className="text-sm text-slate-400">No references on file</p>
                      )}
                  </div>
                </div>

                {/* Tags */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Tag className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(matter.tags || []).length > 0 ? (
                      matter.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium"
                        >
                          <Tag className="h-3 w-3" />
                          {tag}
                        </span>
                      ))
                    ) : (
                      <p className="text-sm text-slate-400">No tags</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 4: Notes */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <StickyNote className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                  Notes
                </h3>
                <textarea
                  value={descriptionValue}
                  onChange={(e) => setDescriptionValue(e.target.value)}
                  onBlur={handleDescriptionSave}
                  placeholder="Add notes about this matter..."
                  rows={4}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
                />
              </div>
            </div>
          )}

          {activeTab === "tasks" && id && <TaskBoard matterId={id} />}

          {/* ─── Documents Tab ─── */}
          {activeTab === "documents" && id && (
            <MatterDocumentsTab
              matterId={id}
              clientId={client?._id?.toString() ?? null}
              matterTitle={matter?.title}
            />
          )}

          {/* ─── Document Checklist Tab ─── */}
          {activeTab === "checklist" && (
            <DocumentChecklistPanel
              matterId={id!}
              checklist={checklist}
              setChecklist={setChecklist}
              checklistLoading={checklistLoading}
              client={client}
              onShowAddEmailModal={() => setShowAddEmailModal(true)}
            />
          )}

          {/* ─── Billing Tab ─── */}
          {activeTab === "billing" && (
            <div className="space-y-4">
              {billingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {(() => {
                      const totalInvoicedCents = invoices.reduce((s: number, inv: any) => s + (inv.totalCents || 0), 0);
                      const totalPaidCents = payments.filter((p: any) => p.status === "succeeded").reduce((s: number, p: any) => s + (p.amountCents || 0), 0);
                      const outstandingCents = totalInvoicedCents - totalPaidCents;
                      return (
                        <>
                          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Invoiced</p>
                            <p className="text-2xl font-bold text-slate-800 mt-1">{formatMoney(totalInvoicedCents)}</p>
                          </div>
                          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Paid</p>
                            <p className="text-2xl font-bold text-emerald-600 mt-1">{formatMoney(totalPaidCents)}</p>
                          </div>
                          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Outstanding</p>
                            <p className={`text-2xl font-bold mt-1 ${outstandingCents > 0 ? "text-amber-600" : "text-slate-800"}`}>{formatMoney(outstandingCents)}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Invoices */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                    <div className="px-6 py-4 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-800">Invoices ({invoices.length})</h3>
                    </div>
                    {invoices.length === 0 ? (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col items-center py-12"
                      >
                        <div className="relative mb-4">
                          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                          <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                            <Receipt className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                          </div>
                        </div>
                        <h4 className="text-base font-bold text-slate-800 mb-1">No invoices yet</h4>
                        <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Create an invoice from unbilled time entries to start billing for this matter.</p>
                      </motion.div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 text-left">
                              <th className="px-6 py-3 font-medium text-slate-500">Invoice #</th>
                              <th className="px-6 py-3 font-medium text-slate-500">Status</th>
                              <th className="px-6 py-3 font-medium text-slate-500 text-right">Total</th>
                              <th className="px-6 py-3 font-medium text-slate-500 text-right">Paid</th>
                              <th className="px-6 py-3 font-medium text-slate-500 text-right">Balance</th>
                              <th className="px-6 py-3 font-medium text-slate-500">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {invoices.map((inv: any) => {
                              const invStatusColors: Record<string, string> = {
                                draft: "bg-slate-100 text-slate-600",
                                issued: "bg-blue-100 text-blue-700",
                                partial: "bg-amber-100 text-amber-700",
                                paid: "bg-emerald-100 text-emerald-700",
                                void: "bg-red-100 text-red-700",
                              };
                              return (
                                <tr key={inv._id} className="hover:bg-slate-50">
                                  <td className="px-6 py-3 font-mono text-xs">{inv.invoiceNumber || inv._id.slice(-6)}</td>
                                  <td className="px-6 py-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${invStatusColors[inv.status] || "bg-slate-100 text-slate-600"}`}>
                                      {formatLabel(inv.status || "draft")}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 text-right font-medium">{formatMoney(inv.totalCents || 0)}</td>
                                  <td className="px-6 py-3 text-right">{formatMoney(inv.paidCents || 0)}</td>
                                  <td className="px-6 py-3 text-right font-medium">{formatMoney((inv.totalCents || 0) - (inv.paidCents || 0))}</td>
                                  <td className="px-6 py-3 text-slate-500">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : ""}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Payments */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-800">Payments ({payments.length})</h3>
                      {canManageBilling && (
                        <button
                          onClick={() => setShowRecordPayment(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl"
                          style={{ backgroundColor: "var(--color-primary)" }}
                        >
                          <Plus className="h-4 w-4" />
                          Record Payment
                        </button>
                      )}
                    </div>
                    {payments.length === 0 ? (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col items-center py-12"
                      >
                        <div className="relative mb-4">
                          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                          <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                            <CreditCard className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                          </div>
                        </div>
                        <h4 className="text-base font-bold text-slate-800 mb-1">No payments recorded</h4>
                        <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Payments will appear here once invoices are paid by clients.</p>
                      </motion.div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 text-left">
                              <th className="px-6 py-3 font-medium text-slate-500">Date</th>
                              <th className="px-6 py-3 font-medium text-slate-500">Source</th>
                              <th className="px-6 py-3 font-medium text-slate-500 text-right">Amount</th>
                              <th className="px-6 py-3 font-medium text-slate-500">Status</th>
                              <th className="px-6 py-3 font-medium text-slate-500">Invoice</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {payments.map((pay: any) => {
                              const sourceColors: Record<string, string> = {
                                stripe: "bg-purple-100 text-purple-700",
                                cheque: "bg-blue-100 text-blue-700",
                                eft: "bg-teal-100 text-teal-700",
                                cash: "bg-amber-100 text-amber-700",
                                trust_transfer: "bg-emerald-100 text-emerald-700",
                              };
                              return (
                                <tr key={pay._id} className="hover:bg-slate-50">
                                  <td className="px-6 py-3 text-slate-500">{pay.createdAt ? new Date(pay.createdAt).toLocaleDateString() : ""}</td>
                                  <td className="px-6 py-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${sourceColors[pay.source] || "bg-slate-100 text-slate-600"}`}>
                                      {formatLabel(pay.source || "other")}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 text-right font-medium">{formatMoney(pay.amountCents || 0)}</td>
                                  <td className="px-6 py-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      pay.status === "succeeded" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                                    }`}>
                                      {formatLabel(pay.status || "pending")}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 font-mono text-xs text-slate-500">
                                    {pay.invoiceId ? (typeof pay.invoiceId === "object" ? pay.invoiceId.invoiceNumber : pay.invoiceId.slice(-6)) : " - "}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "timeline" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Matter Timeline</h3>
              {timeline?.tasks?.length > 0 ? (
                <div className="space-y-3">
                  {timeline.tasks.map((task: any) => (
                    <div key={task._id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="h-2 w-2 rounded-full bg-brand-accent mt-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{task.title}</p>
                        <p className="text-xs text-slate-500">
                          {task.status} - {new Date(task.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="flex flex-col items-center py-12"
                >
                  <div className="relative mb-4">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                    <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                      <Clock className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                    </div>
                  </div>
                  <h4 className="text-base font-bold text-slate-800 mb-1">No timeline events yet</h4>
                  <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Activity and milestones for this matter will appear here as work progresses.</p>
                </motion.div>
              )}
            </div>
          )}

          {activeTab === "parties" && id && (
            <PartiesTab
              matterId={id}
              matter={matter}
              client={client}
              onMatterUpdate={setMatter}
            />
          )}

          {activeTab === "undertakings" && id && (
            <UndertakingsTab matterId={id} />
          )}

          {activeTab === "notes" && id && <MatterNotesTab matterId={id} />}

          {/* ─── Communications Tab ─── */}
          {activeTab === "communications" && (
            <div className="space-y-6">
              {commsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {/* Primary client */}
                  {client && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: "var(--color-accent)" }}
                          >
                            {getInitials(client.displayName)}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{client.displayName}</p>
                            <p className="text-xs text-slate-500">Primary Client</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {client.emails?.[0]?.email && (
                            <button
                              onClick={() => openCompose({ to: [client.emails[0].email], clientId: client._id })}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                              <Mail className="h-4 w-4" />
                              Email
                            </button>
                          )}
                          {client.phones?.[0]?.number && (
                            <button
                              onClick={() => navigate(`/communications?compose=sms&clientId=${client._id}`)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                              <Send className="h-4 w-4" />
                              SMS
                            </button>
                          )}
                        </div>
                      </div>
                      {(clientConversations[client._id] || []).length > 0 ? (
                        <div className="space-y-2">
                          {clientConversations[client._id].slice(0, 5).map((conv: any, i: number) => (
                            <div key={conv._id || i} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-slate-500 uppercase">
                                  {conv.channels?.primaryChannel || "message"}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                                </span>
                              </div>
                              <p className="text-sm text-slate-700 truncate">{conv.preview || conv.subject || "No preview"}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex flex-col items-center py-6"
                        >
                          <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 mb-3">
                            <MessageSquare className="h-6 w-6" style={{ color: "var(--color-primary)" }} />
                          </div>
                          <p className="text-sm font-medium text-slate-600 mb-1">No conversations yet</p>
                          <p className="text-xs text-slate-400">Start a conversation via email or SMS</p>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Linked clients */}
                  {(matter.clientLinks || []).map((link: any, i: number) => {
                    const linkClient = typeof link.clientId === "object" ? link.clientId : null;
                    if (!linkClient) return null;
                    return (
                      <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 font-bold text-sm">
                              {getInitials(linkClient.displayName)}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">{linkClient.displayName}</p>
                              <p className="text-xs text-slate-500 capitalize">{link.role}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {linkClient.emails?.[0]?.email && (
                              <button
                                onClick={() => openCompose({ to: [linkClient.emails[0].email], clientId: linkClient._id })}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                              >
                                <Mail className="h-4 w-4" />
                                Email
                              </button>
                            )}
                            {linkClient.phones?.[0]?.number && (
                              <button
                                onClick={() => navigate(`/communications?compose=sms&clientId=${linkClient._id}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                              >
                                <Send className="h-4 w-4" />
                                SMS
                              </button>
                            )}
                          </div>
                        </div>
                        {(clientConversations[linkClient._id] || []).length > 0 ? (
                          <div className="space-y-2">
                            {clientConversations[linkClient._id].slice(0, 5).map((conv: any, ci: number) => (
                              <div key={conv._id || ci} className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-slate-500 uppercase">
                                    {conv.channels?.primaryChannel || "message"}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-700 truncate">{conv.preview || conv.subject || "No preview"}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="flex flex-col items-center py-6"
                          >
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 mb-3">
                              <MessageSquare className="h-6 w-6 text-slate-400" />
                            </div>
                            <p className="text-sm font-medium text-slate-600 mb-1">No conversations yet</p>
                            <p className="text-xs text-slate-400">Start a conversation via email or SMS</p>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}

                  {!client && (matter.clientLinks || []).length === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-16"
                    >
                      <div className="relative mb-5">
                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                        <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                          <MessageSquare className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">No clients linked</h3>
                      <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                        Link a client to this matter to start communicating via email or SMS.
                      </p>
                    </motion.div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── Immigration: Milestones Tab ─── */}
          {activeTab === "imm-milestones" && immigrationCase && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Milestones ({immigrationCase.milestones?.length || 0})</h3>
                <button
                  onClick={() => setShowMilestoneForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  <Plus className="h-4 w-4" />
                  Add Milestone
                </button>
              </div>

              {immigrationCase.milestones?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-slate-700">
                      {immigrationCase.milestones.filter((m: any) => m.status === "done").length} of {immigrationCase.milestones.length} completed
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2.5">
                    <div
                      className="bg-emerald-500 h-2.5 rounded-full transition-all"
                      style={{ width: `${(immigrationCase.milestones.filter((m: any) => m.status === "done").length / immigrationCase.milestones.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {immigrationCase.milestones?.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center py-14"
                >
                  <div className="relative mb-5">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                    <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                      <Milestone className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                    </div>
                  </div>
                  <h4 className="text-base font-bold text-slate-800 mb-1">No milestones yet</h4>
                  <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Add milestones to track key steps and deadlines in this case.</p>
                </motion.div>
              ) : (
                <DragDropContext onDragEnd={handleMilestoneReorder}>
                  <Droppable droppableId="milestones">
                    {(provided) => (
                      <div className="space-y-2" ref={provided.innerRef} {...provided.droppableProps}>
                        {immigrationCase.milestones?.map((ms: any, index: number) => {
                          const isOverdue = ms.dueAt && ms.status !== "done" && new Date(ms.dueAt).getTime() < Date.now();
                          const isUpcoming = ms.dueAt && ms.status !== "done" && !isOverdue && new Date(ms.dueAt).getTime() - Date.now() < 7 * 86400000;
                          return (
                            <Draggable key={ms.key || `ms-${index}`} draggableId={ms.key || `ms-${index}`} index={index}>
                              {(dragProvided, snapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  className={`bg-white rounded-xl shadow-sm border p-4 flex items-center gap-3 ${
                                    isOverdue ? "border-red-200" : isUpcoming ? "border-amber-200" : "border-slate-200"
                                  } ${snapshot.isDragging ? "shadow-lg ring-2 ring-emerald-200" : ""}`}
                                >
                                  <div {...dragProvided.dragHandleProps} className="flex-shrink-0 cursor-grab text-slate-300 hover:text-slate-500">
                                    <GripVertical className="h-5 w-5" />
                                  </div>
                                  <button
                                    onClick={() => handleMilestoneStatus(index, cycleMilestoneStatus(ms.status))}
                                    className="flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                    title={`Click to change status (${ms.status.replace("_", " ")})`}
                                  >
                                    {milestoneStatusIcons[ms.status] || milestoneStatusIcons.not_started}
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    {editingMilestoneIdx === index ? (
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="text"
                                          value={editMilestoneLabel}
                                          onChange={(e) => setEditMilestoneLabel(e.target.value)}
                                          onKeyDown={(e) => { if (e.key === "Enter") handleEditMilestoneLabel(index); if (e.key === "Escape") setEditingMilestoneIdx(null); }}
                                          className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:border-emerald-500"
                                          autoFocus
                                        />
                                        <button onClick={() => handleEditMilestoneLabel(index)} className="text-emerald-600 hover:text-emerald-700">
                                          <Save className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => setEditingMilestoneIdx(null)} className="text-slate-400 hover:text-slate-600">
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      <p
                                        className="font-medium text-slate-800 cursor-pointer hover:text-emerald-700 transition-colors"
                                        onClick={() => { setEditingMilestoneIdx(index); setEditMilestoneLabel(ms.label); }}
                                        title="Click to edit"
                                      >
                                        {ms.label}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                      <span className="font-mono">{ms.key}</span>
                                      {ms.dueAt && (
                                        <span className={isOverdue ? "text-red-600 font-medium" : isUpcoming ? "text-amber-600 font-medium" : ""}>
                                          Due: {new Date(ms.dueAt).toLocaleDateString()}
                                        </span>
                                      )}
                                      {ms.completedAt && (
                                        <span className="text-emerald-600">
                                          Completed: {new Date(ms.completedAt).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    {ms.notes && <p className="text-xs text-slate-500 mt-1">{ms.notes}</p>}
                                  </div>
                                  <div className="flex-shrink-0 w-36">
                                    <SearchableSelect
                                      options={[
                                        { value: "not_started", label: "Not Started" },
                                        { value: "in_progress", label: "In Progress" },
                                        { value: "done", label: "Done" },
                                        { value: "blocked", label: "Blocked" },
                                      ]}
                                      value={ms.status}
                                      onChange={(val) => { if (val) handleMilestoneStatus(index, val); }}
                                      placeholder="Status"
                                    />
                                  </div>
                                  <button
                                    onClick={() => handleRemoveMilestone(index)}
                                    className="flex-shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-600 transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              {/* Add Milestone Form */}
              <AnimatePresence>
                {showMilestoneForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                  >
                    <h4 className="font-semibold text-slate-800 mb-4">Add Milestone</h4>
                    <form onSubmit={handleAddMilestone} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Key *</label>
                        <input type="text" required value={milestoneForm.key} onChange={(e) => setMilestoneForm({ ...milestoneForm, key: e.target.value })} placeholder="e.g. medical_exam" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Label *</label>
                        <input type="text" required value={milestoneForm.label} onChange={(e) => setMilestoneForm({ ...milestoneForm, label: e.target.value })} placeholder="Medical Exam" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                        <input type="date" value={milestoneForm.dueAt} onChange={(e) => setMilestoneForm({ ...milestoneForm, dueAt: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div className="sm:col-span-3 flex justify-end gap-3">
                        <button type="button" onClick={() => setShowMilestoneForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                        <button type="submit" disabled={immSaving || !milestoneForm.key || !milestoneForm.label} className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50" style={{ backgroundColor: "var(--color-primary)" }}>{immSaving ? "Adding..." : "Add Milestone"}</button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ─── Immigration: Filings Tab ─── */}
          {activeTab === "imm-filings" && immigrationCase && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Filings ({immigrationCase.filings?.length || 0})</h3>
                <button onClick={() => setShowFilingForm(true)} className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl" style={{ backgroundColor: "var(--color-primary)" }}>
                  <Plus className="h-4 w-4" />
                  Add Filing
                </button>
              </div>

              {immigrationCase.filings?.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center py-14"
                >
                  <div className="relative mb-5">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                    <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                      <FileText className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                    </div>
                  </div>
                  <h4 className="text-base font-bold text-slate-800 mb-1">No filings yet</h4>
                  <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Add filings to track submissions and their outcomes.</p>
                </motion.div>
              ) : (
                <div className="space-y-3">
                  {immigrationCase.filings?.map((filing: any, index: number) => (
                    <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{formatLabel(filing.category || "application")}</span>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${filingOutcomeColors[filing.outcome?.status || "pending"] || "bg-slate-100 text-slate-600"}`}>{formatLabel(filing.outcome?.status || "pending")}</span>
                          <span className="text-xs text-slate-500">{filing.authority || "IRCC"}</span>
                        </div>
                        <button onClick={() => handleRemoveFiling(index)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {filing.submittedAt && (<div><span className="text-slate-500 block text-xs">Submitted</span><span className="text-slate-700">{new Date(filing.submittedAt).toLocaleDateString()}</span></div>)}
                        {filing.receivedAt && (<div><span className="text-slate-500 block text-xs">Received</span><span className="text-slate-700">{new Date(filing.receivedAt).toLocaleDateString()}</span></div>)}
                        {filing.decisionAt && (<div><span className="text-slate-500 block text-xs">Decision</span><span className="text-slate-700">{new Date(filing.decisionAt).toLocaleDateString()}</span></div>)}
                        {filing.referenceNumbers?.applicationNumber && (<div><span className="text-slate-500 block text-xs">App #</span><span className="text-slate-700 font-mono">{filing.referenceNumbers.applicationNumber}</span></div>)}
                        {filing.referenceNumbers?.uci && (<div><span className="text-slate-500 block text-xs">UCI</span><span className="text-slate-700 font-mono">{filing.referenceNumbers.uci}</span></div>)}
                      </div>
                      {filing.notes && <p className="text-sm text-slate-600 mt-3 pt-3 border-t border-slate-100">{filing.notes}</p>}
                      {filing.outcome?.refusalReasons?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs font-medium text-red-600 mb-1">Refusal Reasons:</p>
                          <ul className="list-disc list-inside text-sm text-red-600">
                            {filing.outcome.refusalReasons.map((r: string, ri: number) => <li key={ri}>{r}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Filing Form */}
              <AnimatePresence>
                {showFilingForm && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h4 className="font-semibold text-slate-800 mb-4">Add Filing</h4>
                    <form onSubmit={handleAddFiling} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                        <input type="text" required value={filingForm.jurisdiction.country} onChange={(e) => setFilingForm({ ...filingForm, jurisdiction: { ...filingForm.jurisdiction, country: e.target.value } })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Authority</label>
                        <input type="text" value={filingForm.authority} onChange={(e) => setFilingForm({ ...filingForm, authority: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                        <SearchableSelect
                          options={[
                            { value: "application", label: "Application" },
                            { value: "extension", label: "Extension" },
                            { value: "appeal", label: "Appeal" },
                            { value: "response", label: "Response" },
                            { value: "webform", label: "Webform" },
                            { value: "other", label: "Other" },
                          ]}
                          value={filingForm.category}
                          onChange={(val) => setFilingForm({ ...filingForm, category: val || "application" })}
                          placeholder="Select category"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Submission Method</label>
                        <SearchableSelect
                          options={[
                            { value: "online_portal", label: "Online Portal" },
                            { value: "paper", label: "Paper" },
                            { value: "authorized_rep_portal", label: "Authorized Rep Portal" },
                            { value: "email", label: "Email" },
                            { value: "other", label: "Other" },
                          ]}
                          value={filingForm.submissionMethod}
                          onChange={(val) => setFilingForm({ ...filingForm, submissionMethod: val || "online_portal" })}
                          placeholder="Select method"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                        <textarea value={filingForm.notes} onChange={(e) => setFilingForm({ ...filingForm, notes: e.target.value })} rows={2} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div className="sm:col-span-2 flex justify-end gap-3">
                        <button type="button" onClick={() => setShowFilingForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                        <button type="submit" disabled={immSaving} className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50" style={{ backgroundColor: "var(--color-primary)" }}>{immSaving ? "Adding..." : "Add Filing"}</button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ─── Immigration: Parties & Dependents Tab ─── */}
          {activeTab === "imm-parties" && immigrationCase && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Parties & Dependents ({immigrationCase.parties?.length || 0})</h3>
                <button onClick={() => setShowPartyForm(true)} className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl" style={{ backgroundColor: "var(--color-primary)" }}>
                  <Plus className="h-4 w-4" />
                  Add Party
                </button>
              </div>

              {immigrationCase.parties?.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center py-14"
                >
                  <div className="relative mb-5">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-50" />
                    <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                      <Users className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                    </div>
                  </div>
                  <h4 className="text-base font-bold text-slate-800 mb-1">No parties added yet</h4>
                  <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Add the principal applicant, dependents, and other parties involved in this case.</p>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {immigrationCase.parties?.map((party: any, index: number) => (
                    <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{formatLabel(party.role)}</span>
                        <button onClick={() => handleRemoveParty(index)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <h4 className="font-semibold text-slate-800">{party.displayName || "Unnamed"}</h4>
                      <div className="space-y-1 text-sm text-slate-600 mt-2">
                        {party.email && <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />{party.email}</p>}
                        {party.phone && <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{party.phone}</p>}
                        {party.citizenshipCountry && <p>Citizenship: {party.citizenshipCountry}</p>}
                        {party.residenceCountry && <p>Residence: {party.residenceCountry}</p>}
                        {party.dateOfBirth && <p className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-slate-400" />DOB: {new Date(party.dateOfBirth).toLocaleDateString()}</p>}
                        {party.passportNumber && <p>Passport: {party.passportNumber}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Party Form */}
              <AnimatePresence>
                {showPartyForm && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h4 className="font-semibold text-slate-800 mb-4">Add Party / Dependent</h4>
                    <form onSubmit={handleAddParty} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                        <SearchableSelect
                          options={[
                            { value: "principal_applicant", label: "Principal Applicant" },
                            { value: "spouse_partner", label: "Spouse/Partner" },
                            { value: "dependent_child", label: "Dependent Child" },
                            { value: "sponsor", label: "Sponsor" },
                            { value: "employer", label: "Employer" },
                            { value: "representative", label: "Representative" },
                            { value: "other", label: "Other" },
                          ]}
                          value={partyForm.role}
                          onChange={(val) => setPartyForm({ ...partyForm, role: val || "principal_applicant" })}
                          placeholder="Select role"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                        <input type="text" required value={partyForm.displayName} onChange={(e) => setPartyForm({ ...partyForm, displayName: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input type="email" value={partyForm.email} onChange={(e) => setPartyForm({ ...partyForm, email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                        <input type="text" value={partyForm.phone} onChange={(e) => setPartyForm({ ...partyForm, phone: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Citizenship Country</label>
                        <input type="text" value={partyForm.citizenshipCountry} onChange={(e) => setPartyForm({ ...partyForm, citizenshipCountry: e.target.value })} placeholder="e.g. IN, PH, NG" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Residence Country</label>
                        <input type="text" value={partyForm.residenceCountry} onChange={(e) => setPartyForm({ ...partyForm, residenceCountry: e.target.value })} placeholder="e.g. CA, US" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Date of Birth</label>
                        <input type="date" value={partyForm.dateOfBirth} onChange={(e) => setPartyForm({ ...partyForm, dateOfBirth: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Passport Number</label>
                        <input type="text" value={partyForm.passportNumber} onChange={(e) => setPartyForm({ ...partyForm, passportNumber: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
                      </div>
                      <div className="sm:col-span-2 flex justify-end gap-3">
                        <button type="button" onClick={() => setShowPartyForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                        <button type="submit" disabled={immSaving || !partyForm.displayName} className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50" style={{ backgroundColor: "var(--color-primary)" }}>{immSaving ? "Adding..." : "Add Party"}</button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ─── Immigration: Document Checklist Tab ─── */}
          {activeTab === "imm-documents" && (
            <DocumentChecklistPanel
              matterId={id!}
              checklist={checklist}
              setChecklist={setChecklist}
              checklistLoading={checklistLoading}
              client={client}
              onShowAddEmailModal={() => setShowAddEmailModal(true)}
            />
          )}

          {/* ─── Immigration: Program Data Tab ─── */}
          {activeTab === "imm-program-data" && immigrationCase && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Program Data - {immigrationCase.program?.programKey}</h3>
                <button onClick={handleSaveProgramData} disabled={immSaving} className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl disabled:opacity-50" style={{ backgroundColor: "var(--color-primary)" }}>
                  <Save className="h-4 w-4" />
                  {immSaving ? "Saving..." : "Save Data"}
                </button>
              </div>

              {programSchema.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center py-14"
                >
                  <div className="relative mb-5">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 blur-xl scale-150 opacity-50" />
                    <div className="relative p-4 rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 shadow-sm">
                      <Database className="h-8 w-8 text-slate-400" />
                    </div>
                  </div>
                  <h4 className="text-base font-bold text-slate-800 mb-1">No program schema fields found</h4>
                  <p className="text-sm text-slate-500 text-center max-w-xs leading-relaxed">Seed programs first or check if this program has schema fields defined.</p>
                </motion.div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {programSchema.map((field: any) => {
                      const val = programData.data?.[field.key] ?? "";
                      const onChange = (v: any) => {
                        setProgramData((prev: any) => ({ ...prev, data: { ...prev.data, [field.key]: v } }));
                      };
                      return (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.helpText && <p className="text-xs text-slate-400 mb-1">{field.helpText}</p>}
                          {field.type === "text" && <input type="text" value={val} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />}
                          {field.type === "number" && <input type="number" value={val} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />}
                          {field.type === "money" && <input type="number" value={typeof val === "number" ? val / 100 : ""} onChange={(e) => onChange(Math.round(parseFloat(e.target.value || "0") * 100))} step="0.01" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />}
                          {field.type === "date" && <input type="date" value={val ? val.substring(0, 10) : ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />}
                          {field.type === "boolean" && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!val} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600" />
                              <span className="text-sm text-slate-700">Yes</span>
                            </label>
                          )}
                          {field.type === "select" && (
                            <SearchableSelect options={(field.options || []).map((opt: string) => ({ value: opt, label: opt }))} value={val || null} onChange={(v) => onChange(v || "")} placeholder="Select..." clearable />
                          )}
                          {field.type === "multiselect" && (
                            <div className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white space-y-2 max-h-40 overflow-y-auto">
                              {field.options?.map((opt: string) => {
                                const selected = Array.isArray(val) ? val : [];
                                const isChecked = selected.includes(opt);
                                return (
                                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={isChecked} onChange={() => { const next = isChecked ? selected.filter((v: string) => v !== opt) : [...selected, opt]; onChange(next); }} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600" />
                                    <span className="text-sm text-slate-700">{opt}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Immigration fallback: no linked case */}
          {(activeTab === "imm-milestones" || activeTab === "imm-filings" || activeTab === "imm-parties" || activeTab === "imm-program-data") && !immigrationCase && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-100 to-amber-50 blur-xl scale-150 opacity-50" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 shadow-sm">
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                </div>
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-1">No immigration case linked</h4>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">Create an immigration case from the Immigration tab to see data here.</p>
            </motion.div>
          )}

          {/* Real Estate tabs */}
          {activeTab === "re-overview" && realEstateCase && (
            <RealEstateOverviewTab matterId={id!} reCase={realEstateCase} onUpdate={fetchRealEstateCase} />
          )}
          {activeTab === "re-pipeline" && (
            <RealEstatePipelineTab matterId={id!} reCase={realEstateCase || {}} />
          )}
          {activeTab === "re-checklist" && (
            <RealEstateChecklistTab matterId={id!} reCase={realEstateCase || {}} />
          )}
          {activeTab === "re-documents" && realEstateCase && (
            <RealEstateDocumentsTab matterId={id!} reCase={realEstateCase} />
          )}
          {activeTab === "re-funds" && realEstateCase && (
            <RealEstateFundsTab matterId={id!} reCase={realEstateCase} onUpdate={fetchRealEstateCase} />
          )}
          {activeTab === "re-title" && realEstateCase && (
            <RealEstateTitleTab matterId={id!} reCase={realEstateCase} onUpdate={fetchRealEstateCase} />
          )}

          {/* Real estate fallback: tabs that require a linked case */}
          {["re-overview", "re-documents", "re-funds", "re-title"].includes(activeTab) && !realEstateCase && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-100 to-amber-50 blur-xl scale-150 opacity-50" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 shadow-sm">
                  <Home className="h-8 w-8 text-amber-500" />
                </div>
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-1">No real estate case linked</h4>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">Create a real estate case using the Real Estate wizard to see data here.</p>
            </motion.div>
          )}

          {/* Estates tabs */}
          {activeTab === "estates-parties" && estateCase && (
            <EstatePartiesTab matterId={id!} caseData={estateCase} onUpdate={fetchEstateCase} />
          )}
          {activeTab === "estates-capacity" && estateCase && (
            <CapacityAssessmentTab matterId={id!} caseData={estateCase} onUpdate={fetchEstateCase} />
          )}
          {activeTab === "estates-signing" && estateCase && (
            <SigningCeremonyTab matterId={id!} caseData={estateCase} onUpdate={fetchEstateCase} />
          )}
          {activeTab === "estates-documents" && estateCase && (
            <EstateDocumentPackageTab matterId={id!} caseData={estateCase} onUpdate={fetchEstateCase} />
          )}
          {activeTab === "estates-custody" && estateCase && (
            <EstateCustodyTab matterId={id!} caseData={estateCase} onUpdate={fetchEstateCase} />
          )}
          {activeTab === "estates-deadlines" && estateCase && (
            <EstateDeadlinesTab matterId={id!} caseData={estateCase} onUpdate={fetchEstateCase} />
          )}

          {/* Estates fallback: no linked case */}
          {activeTab.startsWith("estates-") && !estateCase && !["estates-will", "estates-beneficiaries", "estates-assets", "estates-distributions"].includes(activeTab) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-100 to-amber-50 blur-xl scale-150 opacity-50" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 shadow-sm">
                  <ScrollText className="h-8 w-8 text-amber-500" />
                </div>
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-1">No estate case linked</h4>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">Create an estate case from the Wills & Estates practice area to see data here.</p>
            </motion.div>
          )}

          {/* Practice-specific tabs  -  placeholder content */}
          {activeTab.startsWith("criminal-") ||
          activeTab.startsWith("pi-") ||
          activeTab.startsWith("family-") ||
          activeTab.startsWith("lit-") ||
          activeTab === "estates-will" ||
          activeTab === "estates-beneficiaries" ||
          activeTab === "estates-assets" ||
          activeTab === "estates-distributions" ||
          activeTab.startsWith("ca-") ||
          activeTab.startsWith("corp-") ||
          activeTab.startsWith("emp-") ||
          activeTab.startsWith("ip-") ||
          activeTab.startsWith("tax-") ||
          activeTab.startsWith("env-") ||
          activeTab.startsWith("bk-") ||
          activeTab.startsWith("admin-") ||
          activeTab.startsWith("const-") ||
          activeTab.startsWith("ins-") ||
          activeTab.startsWith("ent-") ||
          activeTab.startsWith("tech-") ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <FileText className="h-6 w-6 text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">
                {allTabs.find((t) => t.id === activeTab)?.label || "Practice-specific"} data
              </p>
              <p className="text-sm text-slate-400 mt-1">
                This tab will display practice-specific case data
              </p>
            </div>
          ) : null}
        </motion.div>
      </div>

      {/* Record Payment Modal */}
      <PremiumModal open={showRecordPayment} onClose={() => setShowRecordPayment(false)} title="Record Payment" size="md">
        <div className="space-y-4 p-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="number" step="0.01" value={paymentForm.amountDollars} onChange={(e) => setPaymentForm({ ...paymentForm, amountDollars: e.target.value })} placeholder="0.00" className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source *</label>
              <SearchableSelect
                options={[
                  { value: "cheque", label: "Cheque" },
                  { value: "eft", label: "EFT / Wire" },
                  { value: "cash", label: "Cash" },
                  { value: "trust_transfer", label: "Trust Transfer" },
                ]}
                value={paymentForm.source}
                onChange={(val) => setPaymentForm({ ...paymentForm, source: val || "cheque" })}
                placeholder="Select source"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice (optional)</label>
            <SearchableSelect
              options={invoices.map((inv: any) => ({ value: inv._id, label: `${inv.invoiceNumber || inv._id.slice(-6)}  -  ${formatMoney(inv.totalCents || 0)}` }))}
              value={paymentForm.invoiceId || null}
              onChange={(val) => setPaymentForm({ ...paymentForm, invoiceId: val || "" })}
              placeholder="Leave blank for payment on account"
              clearable
            />
          </div>
          {paymentForm.source === "cheque" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cheque Number</label>
              <input type="text" value={paymentForm.chequeNumber} onChange={(e) => setPaymentForm({ ...paymentForm, chequeNumber: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
          )}
          {paymentForm.source === "eft" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">EFT Reference</label>
              <input type="text" value={paymentForm.eftReference} onChange={(e) => setPaymentForm({ ...paymentForm, eftReference: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Received From</label>
            <input type="text" value={paymentForm.receivedFrom} onChange={(e) => setPaymentForm({ ...paymentForm, receivedFrom: e.target.value })} placeholder="Name of payer" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} rows={2} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Receipt (optional)</label>
            <label className="flex items-center gap-2 px-4 py-3 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
              <Upload className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-500">{receiptFile ? receiptFile.name : "Upload receipt file..."}</span>
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowRecordPayment(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={handleRecordPayment} disabled={creatingPayment || !paymentForm.amountDollars || parseFloat(paymentForm.amountDollars) <= 0} className="px-6 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50" style={{ backgroundColor: "var(--color-primary)" }}>{creatingPayment ? "Recording..." : "Record Payment"}</button>
          </div>
        </div>
      </PremiumModal>

      {/* Delete Confirmation Modal */}
      <PremiumModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Matter"
        icon={Trash2}
        size="sm"
        footer={
          isAdmin ? (
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Yes, Delete Matter"}
              </button>
            </div>
          ) : (
            <div className="flex justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          )
        }
      >
        {isAdmin ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">This action cannot be undone</p>
                <p className="text-sm text-red-600 mt-1">
                  Deleting this matter will remove it from all views, including associated tasks, time entries, and documents. Invoices linked to this matter will remain but will no longer be associated.
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Are you sure you want to delete <span className="font-semibold">{matter?.title}</span>?
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Administrator access required</p>
              <p className="text-sm text-amber-600 mt-1">
                Only firm administrators can delete matters. Please contact your administrator to remove this matter.
              </p>
            </div>
          </div>
        )}
      </PremiumModal>

      {/* Add Client Email Modal */}
      <PremiumModal open={showAddEmailModal} onClose={() => setShowAddEmailModal(false)} title="Add Client Email" subtitle="Required to send document reminders" icon={Mail} size="sm">
        <div className="space-y-4 p-1">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-500" />
            <span>This client has no email address. Add one to send document reminders.</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
            <input
              type="email"
              value={addEmailValue}
              onChange={(e) => setAddEmailValue(e.target.value)}
              placeholder="client@example.com"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              onKeyDown={(e) => { if (e.key === "Enter") handleAddClientEmail(); }}
            />
          </div>
          {addEmailError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{addEmailError}</p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAddEmailModal(false); setAddEmailError(""); }}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddClientEmail}
              disabled={!addEmailValue.trim() || addingEmail}
              className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {addingEmail ? "Adding..." : "Add Email & Send Reminder"}
            </button>
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default MatterDetail;
